import { useDispatch, useSelector } from "react-redux"
import { selectUsers, selectWineries } from "../../context/selectors"
import useLabels from "../../hooks/useLabels";
import { useNavigate } from "react-router-dom";
import { set, setActive } from "../../context/wineries/wineriesSlice";
import { useEffect, useState } from "react";
import { getUsers, getWineries } from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { ActionButton } from "@wine2trace/ui/FormComponents";
import SearchableTable from "../DataTable/SearchableTable";
import { emptyTanks } from "../../context/tanks/tanksSlice";
import { emptyLots } from "../../context/lots/lotsSlice";
import { emptyUsers, setUsers } from "../../context/users/usersSlice";
import { emptyAnalysis } from "../../context/protocols/analysisSlice";
import { emptyExp } from "../../context/expOperations/expOperationsSlice";
import { empty } from "../../context/operations/operationsSlice";
import './styles/index.scss';
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";

// Street, cap and city of a winery, without the separators of the missing parts.
const wineryAddress = (winery) =>
    `${winery?.address ?? ""}, ${winery?.cap ?? ""} ${winery?.city ?? ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, "");

// Winery chooser of the Home modal: a row per winery (sorted by name), the whole row choosing it (its name is
// the button for the keyboard); a search when the list is long, on mobile a block per winery. No wineries: the alert, with "new winery" for a client.
const WineriesSelector = ({ eventCallback }) => {

    const { auth } = useAuth();
    const role = auth?.role || ROLES.GUEST;
    const wineries = useSelector(selectWineries);
    const users = useSelector(selectUsers);
    const dispatch = useDispatch();
    const [getLabel] = useLabels();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const [isWineriesLoading, setIsWineriesLoading] = useState(true);

    const choose = (winery) => {
        dispatch(setActive(winery))
        if (eventCallback) {
            eventCallback(winery);

            dispatch(empty());
            dispatch(emptyExp());
            dispatch(emptyAnalysis());
            dispatch(emptyUsers());
            dispatch(emptyTanks());
            dispatch(emptyLots());
        }
    }

    const loadWineries = async () => {
        try {
            const currentWineries = await getWineries(axiosPrivate);
            dispatch(set(currentWineries))
        } finally {
            setIsWineriesLoading(false);
        }
    }

    const loadUsers = async () => {
        const currentUsers = await getUsers(axiosPrivate);
        dispatch(setUsers(currentUsers))
    }

    useEffect(() => {
        if (!wineries || wineries.length === 0) {
            loadWineries();
        } else {
            setIsWineriesLoading(false);
        }

        if (!users || users.length === 0) {
            loadUsers();
        }
    }, [])

    // No wineries means no active winery, so no permissions to read: as the /wineries/new route (allowWithoutWinery)
    // and the BE (verifyRole(ROLE_CLIENT), no permission asked without a winery) only the CLIENT tier decides.
    const canCreateWinery = role === ROLES.CLIENT;

    if (!isWineriesLoading && !wineries?.length) {
        return (
            <div className="wineries_selector_empty">
                <p className="list_empty">{getLabel("noWineriesAlert")}</p>
                {canCreateWinery && (
                    <ActionButton variant="primary" icon="fa-plus" onClick={() => navigate('/wineries/new')}>
                        {getLabel("createWinery")}
                    </ActionButton>
                )}
            </div>
        );
    }

    const columns = [
        {
            key: "name",
            header: getLabel("wineryName"),
            render: (winery) => (
                <button type="button" className="wineries_selector_choose">
                    {winery.name}
                </button>
            ),
        },
        { key: "client", header: getLabel("wineryClientName"), render: (winery) => winery.client_name || "—" },
        { key: "address", header: getLabel("wineryAddress"), render: (winery) => wineryAddress(winery) || "—" },
    ];

    return (
        <div className="wineries_selector">
            <SearchableTable
                rows={[...(wineries || [])].sort((a, b) => a?.name?.localeCompare(b?.name))}
                columns={columns}
                getRowKey={(winery) => winery.id}
                ariaLabel={getLabel("wineriesModalSelector")}
                isLoading={isWineriesLoading}
                searchValues={(winery) => [winery.name, winery.client_name, winery.city]}
                onRowClick={choose}
            />
        </div>
    )
}

export {
    WineriesSelector
}
